/* ==========================================================================
   BRACELY — custom upgrades behaviour
   --------------------------------------------------------------------------
   1. Scroll-triggered reveals (once only, reduced-motion aware)
   2. Interactive "+" hotspots
   3. Email capture pop-up (once per visitor)

   Deliberately dependency-free and defensive: if anything here throws, the
   page still works and no content stays hidden.
   ========================================================================== */

(function () {
  'use strict';

  var prefersReducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ------------------------------------------------------------------------
     1. SCROLL REVEALS
     ---------------------------------------------------------------------- */

  var REVEAL_GROUPS = [
    // "The Essentials" / any product grid card, staggered across the row
    { selector: '.product-grid__card', stagger: 90, maxStagger: 5 },
    // Trust blocks, footer columns and anything else opted in server-side
    { selector: '[data-bracely-reveal]', stagger: 110, maxStagger: 4 },
    // Footer sections
    { selector: 'footer .shopify-section', stagger: 0 }
  ];

  function initReveals() {
    if (prefersReducedMotion || !('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('bracely-reveal--in');
          observer.unobserve(entry.target); // once only
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
    );

    var viewportHeight = window.innerHeight || 800;

    REVEAL_GROUPS.forEach(function (group) {
      var nodes;
      try {
        nodes = document.querySelectorAll(group.selector);
      } catch (e) {
        return;
      }

      var indexInGroup = 0;

      Array.prototype.forEach.call(nodes, function (node) {
        if (node.dataset.bracelyRevealBound === 'true') return;

        // Never hide something that is already on screen — avoids any flash
        // and means above-the-fold content is always immediately readable.
        var rect = node.getBoundingClientRect();
        if (rect.top < viewportHeight * 0.92) {
          node.dataset.bracelyRevealBound = 'true';
          return;
        }

        node.dataset.bracelyRevealBound = 'true';

        if (group.stagger) {
          var step = indexInGroup % (group.maxStagger || 4);
          node.style.setProperty('--bracely-reveal-delay', step * group.stagger + 'ms');
        }

        node.classList.add('bracely-reveal');
        observer.observe(node);
        indexInGroup += 1;
      });
    });
  }

  /* ------------------------------------------------------------------------
     2. HOTSPOTS
     ---------------------------------------------------------------------- */

  function closeAllHotspots(root, except) {
    var buttons = root.querySelectorAll('.bracely-hotspot__button');
    Array.prototype.forEach.call(buttons, function (button) {
      if (button === except) return;
      button.setAttribute('aria-expanded', 'false');
      var hotspot = button.closest('.bracely-hotspot');
      if (hotspot) hotspot.classList.remove('bracely-hotspot--open');
    });
  }

  function initHotspots() {
    var roots = document.querySelectorAll('[data-bracely-hotspots]');
    if (!roots.length) return;

    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.bracelyHotspotsBound === 'true') return;
      root.dataset.bracelyHotspotsBound = 'true';

      root.addEventListener('click', function (event) {
        var button = event.target.closest('.bracely-hotspot__button');
        var closeButton = event.target.closest('.bracely-hotspot__close');

        if (closeButton) {
          event.preventDefault();
          closeAllHotspots(root, null);
          return;
        }

        if (!button) return;
        event.preventDefault();

        var isOpen = button.getAttribute('aria-expanded') === 'true';
        closeAllHotspots(root, button);

        button.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        var hotspot = button.closest('.bracely-hotspot');
        if (hotspot) hotspot.classList.toggle('bracely-hotspot--open', !isOpen);
      });

      root.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closeAllHotspots(root, null);
      });
    });

    // Click outside closes any open card.
    if (!document.body.dataset.bracelyHotspotOutsideBound) {
      document.body.dataset.bracelyHotspotOutsideBound = 'true';
      document.addEventListener('click', function (event) {
        if (event.target.closest('.bracely-hotspot')) return;
        var openRoots = document.querySelectorAll('[data-bracely-hotspots]');
        Array.prototype.forEach.call(openRoots, function (root) {
          closeAllHotspots(root, null);
        });
      });
    }
  }

  /* ------------------------------------------------------------------------
     3. EMAIL POP-UP  (once per visitor)
     ---------------------------------------------------------------------- */

  var POPUP_STORAGE_KEY = 'bracely_popup_seen_v1';

  function storageGet(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private browsing — pop-up simply shows again next session */
    }
  }

  function initPopup() {
    var popup = document.querySelector('[data-bracely-popup]');
    if (!popup) return;
    if (popup.dataset.bracelyPopupBound === 'true') return;
    popup.dataset.bracelyPopupBound = 'true';

    var isDesignMode = document.documentElement.classList.contains('shopify-design-mode');

    // If Shopify has just accepted a signup it reloads the page with the
    // confirmation markup present — always show that, even to a repeat visitor.
    var forceOpen =
      popup.dataset.forceOpen === 'true' || !!popup.querySelector('[data-bracely-popup-success]');

    var delay = parseInt(popup.dataset.delay, 10);
    if (isNaN(delay)) delay = 5000;

    function open() {
      popup.setAttribute('data-open', 'true');
      storageSet(POPUP_STORAGE_KEY, String(Date.now()));
      var field = popup.querySelector('.bracely-popup__field');
      if (field && !forceOpen) {
        window.setTimeout(function () {
          try {
            field.focus({ preventScroll: true });
          } catch (e) {
            /* noop */
          }
        }, 300);
      }
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      popup.setAttribute('data-open', 'false');
      storageSet(POPUP_STORAGE_KEY, String(Date.now()));
      document.removeEventListener('keydown', onKeydown);
    }

    function onKeydown(event) {
      if (event.key === 'Escape') close();
    }

    popup.addEventListener('click', function (event) {
      if (
        event.target.closest('[data-bracely-popup-close]') ||
        event.target.matches('.bracely-popup__backdrop')
      ) {
        event.preventDefault();
        close();
      }
    });

    // Always show the confirmation state, even to a returning visitor.
    if (forceOpen) {
      open();
      return;
    }

    // In the theme editor, show it immediately so it can be styled.
    if (isDesignMode) {
      window.setTimeout(open, 600);
      return;
    }

    if (storageGet(POPUP_STORAGE_KEY)) return; // already seen — never again

    window.setTimeout(open, delay);
  }

  /* ------------------------------------------------------------------------
     BOOT
     ---------------------------------------------------------------------- */

  function boot() {
    try {
      initReveals();
    } catch (e) {
      /* noop */
    }
    try {
      initHotspots();
    } catch (e) {
      /* noop */
    }
    try {
      initPopup();
    } catch (e) {
      /* noop */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Horizon swaps section HTML in the theme editor and on filter/paginate —
  // re-run the idempotent initialisers when that happens.
  document.addEventListener('shopify:section:load', boot);
  document.addEventListener('shopify:section:select', boot);

  function watchMain() {
    if (!('MutationObserver' in window)) return;
    var main = document.getElementById('MainContent');
    if (!main) return;
    var debounce;
    new MutationObserver(function () {
      window.clearTimeout(debounce);
      debounce = window.setTimeout(function () {
        try {
          initReveals();
          initHotspots();
        } catch (e) {
          /* noop */
        }
      }, 120);
    }).observe(main, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchMain);
  } else {
    watchMain();
  }
})();
